// SF1 keyword performance and SF4 impression share, for one product.
//
// One surface, per the PRD's own design note: they share an ASIN input and a
// keyword set, so SF4 is more columns on SF1's rows rather than a second
// screen. No rank is claimed — Amazon exposes organic position through no API,
// and what a term reports is performance.
import React from 'react';
import s from '../Visibility.module.css';
import { num, pct } from '../format';

const VERDICT_STYLE = {
  'seen, not clicked': 'warnTag',
  'clicked, not bought': 'riskTag',
  'barely seen': 'mutedTag',
  holding: 'okTag',
};

const Share = ({ value }) =>
  value == null ? <span className={s.muted}>—</span> : (
    <span className={s.bar}>
      <span className={s.barTrack}>
        <span className={s.barFill} style={{ width: `${Math.min(100, value * 100)}%` }} />
      </span>
      {pct(value)}
    </span>
  );

export default function Keywords({ data, asin }) {
  if (!asin) {
    return (
      <div className={s.empty}>
        Choose a product above to see every search term the report carries for it.
      </div>
    );
  }
  if (!data) return <div className={s.empty}>Loading…</div>;
  if (!data.terms?.length) {
    return (
      <div className={s.empty}>
        <p>Amazon reported no search terms for {asin} in this window.</p>
        <p className={s.hint}>
          That is a result, not a gap: the product received no search impressions. It is
          also what a product with no search visibility looks like.
        </p>
      </div>
    );
  }

  const a = data.summary;
  return (
    <>
      <div className={s.stats}>
        <div className={s.stat}>
          <span className={s.statValue}>{num(a.terms)}</span>
          <span className={s.statLabel}>search terms</span>
        </div>
        <div className={s.stat}>
          <span className={s.statValue}>{pct(a.shares.impressions)}</span>
          <span className={s.statLabel}>impression share</span>
        </div>
        <div className={s.stat}>
          <span className={s.statValue}>{pct(a.shares.clicks)}</span>
          <span className={s.statLabel}>click share</span>
        </div>
        <div className={s.stat}>
          <span className={s.statValue}>{pct(a.top_term_share_of_impressions, 0)}</span>
          <span className={s.statLabel}>on its biggest term</span>
        </div>
      </div>

      <p className={s.hint} style={{ margin: '4px 0 14px' }}>
        Every share here is Amazon&apos;s own arithmetic — the report returns this
        product&apos;s count and the term&apos;s total on the same row, so nothing is
        estimated. Impression share far above click share means the product is shown and
        passed over, which is an image or title problem; the reverse means it wins the
        click and loses the sale. Most impressions ride on{' '}
        <strong>{a.top_term}</strong>.
      </p>

      <div className={s.tableWrap}>
        <table className={s.table}>
          <thead>
            <tr>
              <th>Search term</th>
              <th className={s.numeric}>Volume</th>
              <th className={s.numeric}>Impressions</th>
              <th className={s.numeric}>Impr. share</th>
              <th className={s.numeric}>Clicks</th>
              <th className={s.numeric}>Click share</th>
              <th className={s.numeric}>Cart-add share</th>
              <th className={s.numeric}>Purchase share</th>
              <th className={s.numeric}>Click rate</th>
              <th>Reads as</th>
            </tr>
          </thead>
          <tbody>
            {data.terms.map((t) => (
              <tr key={t.search_term}>
                <td className={s.term}>
                  {t.search_term}
                  {t.term_rank_on_product && (
                    <span className={s.muted} title="Where this term sits among this
                      product's own terms">{' '}#{t.term_rank_on_product}</span>
                  )}
                </td>
                <td className={s.numeric}>{num(t.volume)}</td>
                <td className={s.numeric}>{num(t.own.impressions)}</td>
                <td className={s.numeric}><Share value={t.shares.impressions} /></td>
                <td className={s.numeric}>{num(t.own.clicks)}</td>
                <td className={s.numeric}><Share value={t.shares.clicks} /></td>
                <td className={s.numeric}><Share value={t.shares.cart_adds} /></td>
                <td className={s.numeric}><Share value={t.shares.purchases} /></td>
                <td className={s.numeric}>
                  {pct(t.click_rate)}
                  <span className={s.reading}>market {pct(t.market_click_rate)}</span>
                </td>
                <td>
                  {t.verdict && (
                    <span className={`${s.tagBase} ${s[VERDICT_STYLE[t.verdict]] || ''}`}>
                      {t.verdict}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className={s.hint} style={{ marginTop: 10 }}>
        This surface ends at clicks. Cart-add and purchase shares are shown because the
        funnel needs them to tell one problem from the other, but conversion itself belongs
        to Product Performance Deep Dive.
      </p>
    </>
  );
}
